import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { documentsApi } from '../api/documents';
import DocumentViewer from '../components/DocumentViewer';
import ExtractedFields from '../components/ExtractedFields';
import ValidationResults from '../components/ValidationResults';
import ReviewActions from '../components/ReviewActions';

export default function DocumentReview() {
  const { documentId } = useParams<{ documentId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState<'document' | 'extracted' | 'validation'>('document');

  const { data: document, isLoading } = useQuery({
    queryKey: ['document', documentId],
    queryFn: () => documentsApi.getDocument(documentId!),
    enabled: !!documentId,
  });

  const submitReviewMutation = useMutation({
    mutationFn: documentsApi.submitReview,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['documents'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard-stats'] });
      navigate('/documents');
    },
  });

  const updateDocumentMutation = useMutation({
    mutationFn: ({ documentId, updates }: { documentId: string; updates: Record<string, any> }) =>
      documentsApi.updateDocument(documentId, updates),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['document', documentId] });
    },
  });

  const reprocessMutation = useMutation({
    mutationFn: documentsApi.reprocessDocument,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['document', documentId] });
    },
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (!document) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500">Document not found</p>
      </div>
    );
  }

  const tabs = [
    { id: 'document', name: 'Document View' },
    { id: 'extracted', name: 'Extracted Data' },
    { id: 'validation', name: 'Validation Results' },
  ];

  return (
    <div>
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">{document.filename}</h2>
            <p className="mt-1 text-sm text-gray-500">
              ID: {document.id} • Type: {document.documentType} • Status: {document.status}
            </p>
          </div>
          <button
            onClick={() => navigate('/documents')}
            className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
          >
            Back to List
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200 mb-6">
        <nav className="-mb-px flex space-x-8" aria-label="Tabs">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id as any)}
              className={`
                whitespace-nowrap py-2 px-1 border-b-2 font-medium text-sm
                ${activeTab === tab.id
                  ? 'border-primary-500 text-primary-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }
              `}
            >
              {tab.name}
            </button>
          ))}
        </nav>
      </div>

      {/* Content */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Content */}
        <div className="lg:col-span-2">
          {activeTab === 'document' && (
            <DocumentViewer 
              documentId={document.id}
              attachmentId={document.attachmentId}
              filename={document.filename}
            />
          )}
          {activeTab === 'extracted' && (
            <ExtractedFields
              extractedData={document.extractedData}
              onFieldUpdate={(fieldName, value) => {
                if (document.extractedData) {
                  const updates = {
                    extractedData: {
                      ...document.extractedData,
                      fields: {
                        ...document.extractedData.fields,
                        [fieldName]: {
                          ...document.extractedData.fields[fieldName],
                          value,
                          confidence: 1.0, // Manual update = high confidence
                        },
                      },
                    },
                  };
                  updateDocumentMutation.mutate({ documentId: document.id, updates });
                }
              }}
              isUpdating={updateDocumentMutation.isPending}
            />
          )}
          {activeTab === 'validation' && (
            <ValidationResults validation={document.validation} />
          )}
        </div>

        {/* Sidebar */}
        <div>
          {/* Document Info */}
          <div className="bg-white shadow rounded-lg p-6 mb-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Document Information</h3>
            <dl className="space-y-3">
              <div>
                <dt className="text-sm font-medium text-gray-500">Classification Confidence</dt>
                <dd className="mt-1 text-sm text-gray-900">
                  {(document.classificationConfidence * 100).toFixed(0)}%
                </dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500">Validation Status</dt>
                <dd className="mt-1 text-sm text-gray-900 capitalize">
                  {document.validationStatus}
                </dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500">Created</dt>
                <dd className="mt-1 text-sm text-gray-900">
                  {new Date(document.createdAt).toLocaleString()}
                </dd>
              </div>
              {document.processedAt && (
                <div>
                  <dt className="text-sm font-medium text-gray-500">Processed</dt>
                  <dd className="mt-1 text-sm text-gray-900">
                    {new Date(document.processedAt).toLocaleString()}
                  </dd>
                </div>
              )}
              {document.reviewedAt && (
                <div>
                  <dt className="text-sm font-medium text-gray-500">Reviewed</dt>
                  <dd className="mt-1 text-sm text-gray-900">
                    {new Date(document.reviewedAt).toLocaleString()} by {document.reviewedBy}
                  </dd>
                </div>
              )}
            </dl>
          </div>

          {/* Actions */}
          {document.requiresReview && document.status === 'review_required' && (
            <ReviewActions
              documentId={document.id}
              onApprove={(notes) => {
                submitReviewMutation.mutate({
                  documentId: document.id,
                  approved: true,
                  notes,
                });
              }}
              onReject={(notes) => {
                submitReviewMutation.mutate({
                  documentId: document.id,
                  approved: false,
                  notes,
                });
              }}
              isSubmitting={submitReviewMutation.isPending}
            />
          )}

          {/* Additional Actions */}
          <div className="bg-white shadow rounded-lg p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Actions</h3>
            <div className="space-y-3">
              <button
                onClick={() => reprocessMutation.mutate(document.id)}
                disabled={reprocessMutation.isPending}
                className="w-full inline-flex justify-center items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
              >
                {reprocessMutation.isPending ? 'Reprocessing...' : 'Reprocess Document'}
              </button>
              <button
                onClick={() => {
                  // Handle download
                  window.open(`/api/documents/${document.id}/download`, '_blank');
                }}
                className="w-full inline-flex justify-center items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
              >
                Download Original
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}